// Global const
import {
  formLimitation,
} from "const";

// Local const
import {
  defaultProfile,
  defaultLinkSpeedOptionsDefinitions
} from '../const';

export const getPortType = (({
  selectedPorts
}) => {
  let result = {
    isCopper: false,
    isFiber: false,
    copperPorts: [],
    fibberPorts: [],
  }

  if (!selectedPorts) return result;

  selectedPorts.forEach(port => {

    if (port?.isCopper) {

      result.isCopper = true;
      result.copperPorts.push(port);

    } else if (port?.isFiber) {

      result.isFiber = true;
      result.fibberPorts.push(port);

    }

  });

  return result;
});

export const getProfileRequestParams = ({ auth }) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const apiUrl = `${apiBaseUrl}/sites/:siteId/settings/profilePorts.read`;

  const requestParamsConfig = {
    standalone: {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
    nonStandalone: {
      entId: auth.realm?.id,
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
  };

  const requestParams = isStandaloneMode ? requestParamsConfig["standalone"] : requestParamsConfig["nonStandalone"];

  return {
    apiUrl,
    requestParams
  };
}

export const getInitProfileList = ({
  profileList,
  selectedPort
}) => {
  const filteredProfileList = [defaultProfile, ...profileList].filter(profile => profile.isDefault || profile.deviceCategory === selectedPort.deviceCategory)
  const initProfileList = filteredProfileList.map(profile => {

    return {
      ...structuredClone(profile),
      label: profile?.label ?? profile.name,
      value: profile.value ?? profile.name,
      isSelected: selectedPort?.portProfile ? selectedPort.portProfile === profile.id : !!profile.isDefault,
    }

  })

  return initProfileList;
}

export const checkAllowVlans = (value) => {
  if (!value?.trim()) return false;

  const parts = value.split(",").map((s) => s.trim());
  const used = new Set();
  const { min, max } = formLimitation.vlanId;

  for (let part of parts) {
    if (part === "") return false;

    if (part.includes("-")) {
      const [startStr, endStr] = part.split("-").map(s => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);

      if (
        isNaN(start) ||
        isNaN(end) ||
        start < min ||
        end > max ||
        start > end
      )
        return false;

      for (let i = start; i <= end; i++) {
        if (used.has(i)) return false;
        used.add(i);
      }
    } else {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < min || num > max) return false;

      if (used.has(num)) return false;
      used.add(num);
    }
  }

  return true;
};

export const checkPvid = (value) => {
  let isValid = true;

  if (value === "") {
    isValid = false;
  }

  if (!/^\d+$/.test(value)) {
    isValid = false;
  }

  const intValue = Number(value);
  if (intValue < 1 || intValue > 4094) {
    isValid = false;
  }

  return isValid;
};

export const getInputStyles = ({
  theme,
  isInValid,
  isEdited,
  field,
}) => {

  return {
    width: "300px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        border: isInValid ?
          `1px solid ${theme.palette.primary.redPrimary}` :
          (
            isEdited[field] ?
              `4px solid ${theme.palette.primary.orange}` :
              `1px solid ${theme.palette.primary.borderGreyPrimary}`
          ),
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: isInValid ?
          `1px solid ${theme.palette.primary.redPrimary}` :
          (
            isEdited[field] ?
              `4px solid ${theme.palette.primary.orange}` :
              `1px solid ${theme.palette.primary.mainHover}`
          )
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: isInValid ?
          `1px solid ${theme.palette.primary.redPrimary}` :
          (
            isEdited[field] ?
              `4px solid ${theme.palette.primary.orange}` :
              `1px solid ${theme.palette.primary.main}`
          )
      },
    },
  }
};

export const getIntersectionLinkSpeeds = ({
  selectedPorts,
  isProfileMode = false
}) => {
  if (isProfileMode) {
    // Only for displaying purpose.
    const allList = Object.keys(defaultLinkSpeedOptionsDefinitions).map(key => defaultLinkSpeedOptionsDefinitions[key]);

    return {
      copperList: allList,
      fiberList: allList
    }
  }

  const copperPortList = selectedPorts.filter(port => port.isCopper);
  const fiberPortList = selectedPorts.filter(port => port.isFiber);

  // Get all speeds into one array.
  const flattenCopperList = copperPortList
    .map(port => port.supportedLinkSpeedList.copperList)
    .flat()

  // Get all speeds into one array.
  const flattenFiberList = fiberPortList
    .map(port => port.supportedLinkSpeedList.fiberList)
    .flat()

  return {
    copperList: getIntersectionList({ flattenSpeedList: flattenCopperList, speedList: copperPortList }),
    fiberList: getIntersectionList({ flattenSpeedList: flattenFiberList, speedList: fiberPortList }),
  }
}

export const getIntersectionList = ({
  flattenSpeedList,
  speedList,
}) => {

  // Get the most repeated speed items.
  const intersectionMap = flattenSpeedList
    .reduce((acc, speed) => {
      const speedStr = String(speed);
      acc[speedStr] = acc[speedStr] == null ? 1 : acc[speedStr] += 1;
      return acc
    }, {});

  // Filter the non most repeated speed items out,
  // and change it into { value: xxxxx, label: xxxx, ... };
  const intersectionList = Object.keys(intersectionMap)
    .filter(key => intersectionMap[key] === speedList.length)
    .map(speed => defaultLinkSpeedOptionsDefinitions[speed])
    .sort((a, b) => a.id - b.id);

  return intersectionList;
}